/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXSelector } from '@cloudio-saas/statex';
import { SelectProp } from 'componentregistry';
import { Option, VirtualizedSelectInput } from 'custom/SelectInput';
import { helpTextSelector } from 'designer/state';
import { memo, useEffect, useState } from 'react';
import { itemWithDefaultValues } from 'registry/Components';
import { getDefaultValues, isRequired } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

function SelectProperty({
  appUid,
  activeItemId,
  def,
  pageId,
  property,
  mgr,
  value = getDefaultValues<string | number | boolean>(mgr, property),
  onChange,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<SelectProp, string | number | boolean>) {
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const [state, setState] = useState([]);
  useEffect(() => {
    if (def.optionsRefreshCallback) {
      def.optionsRefreshCallback(() => {
        setState([]);
      });
    }
  }, [def]);
  const [options, setOptions] = useState<Option[]>(() => {
    const _options = def.options;
    return typeof _options === 'function'
      ? []
      : Object.keys(_options).map((k: keyof typeof _options) => ({
          value: k,
          label: String(_options[k]),
        }));
  });

  useEffect(() => {
    if (typeof def.options === 'function') {
      const page = platform.getPage(appUid, pageId);
      const itemWithDefaults = itemWithDefaultValues(page[activeItemId]);
      def
        .options(itemWithDefaults, {
          appUid,
          pageId,
          itemId: activeItemId,
          mgr,
          platform,
        })
        .then((r) => {
          setOptions(
            Object.keys(r).map((k) => ({
              value: k,
              label: String(r[k]),
            })),
          );
        });
    }
  }, [activeItemId, appUid, def, mgr, pageId, platform, state]);

  const handleChange = (_value: string | undefined, option: Option | null) => {
    if (_value === '' || !option) {
      onChange(property, undefined);
    } else {
      onChange(property, option.value as string | number | boolean);
    }
  };
  const required = isRequired({
    platform,
    mgr,
    appUid,
    pageId,
    itemId: activeItemId,
    required: def.required,
  });

  return (
    <VirtualizedSelectInput
      disabled={isViewOnlyUser}
      data-testid={property}
      variant="standard"
      fullWidth
      value={value}
      options={options}
      onChange={handleChange}
      label={def.label}
      placeholder={'Choose an option'}
      required={required}
      helpText={helpText}
    />
  );
}

export default memo(SelectProperty);
